import React, { useEffect, useMemo } from 'react';
import { InputSelect } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';
import { useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { useAssetSchema } from '@Hooks/assetSchemaHooks';
import { usePickerOptions } from '@Hooks/useSystemTypes';
import { PickerStatus } from '@Components/Utils/PickerStatus';
import { FailureDefinitionFormValues } from '@Pages/FailureDefinition';
import { InspectionStandardType } from '@Types/inspections.types';
import { clearFieldMetadataAction } from '@Actions/rehab.actions';

export const SystemTypeField: React.FC = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { systemTypes, status, retry } = usePickerOptions();
	const { values, errors, setFieldValue } = useFormikContext<FailureDefinitionFormValues>();
	const prevSystemTypeRef = React.useRef(values.systemType);

	// Reset dependent fields when systemType changes
	useEffect(() => {
		if (prevSystemTypeRef.current && prevSystemTypeRef.current !== values.systemType) {
			setFieldValue('assetType', '');
			setFieldValue('inspectionStandard', '');
			setFieldValue('events', []);
			setFieldValue('eventDateMap', {});
			setFieldValue('tasks', []);
			setFieldValue('taskDateMap', {});
			// Clear the event and task type options in Redux store
			dispatch(clearFieldMetadataAction());
		}
		prevSystemTypeRef.current = values.systemType;
	}, [values.systemType, setFieldValue, dispatch]);

	return (
		<>
			<InputSelect
				menuItems={systemTypes.map(st => ({
					key: st.id,
					value: st.id,
					label: t(st.displayName),
				}))}
				fieldAttrs={{ name: 'systemType' }}
				selectProps={{
					label: t('System Type'),
					required: true,
					error: !!errors.systemType,
					sx: { minWidth: '200px', maxWidth: '300px', width: '250px' },
				}}
				helperText={errors.systemType ? errors.systemType.toString() : ''}
			/>
			<PickerStatus status={status} retry={retry} />
		</>
	);
};

export const AssetTypeField: React.FC = () => {
	const { t } = useGlobalization();
	const {
		values: { systemType },
		errors,
	} = useFormikContext<FailureDefinitionFormValues>();
	const { assetTypesFor } = usePickerOptions();
	const assetTypes = systemType ? assetTypesFor(systemType) : undefined;

	return (
		<InputSelect
			menuItems={
				assetTypes
					? assetTypes
							.filter(at => at.displayName === 'Pipe')
							.map(at => ({
								key: at.id,
								value: at.id,
								label: t(at.displayName),
							}))
					: []
			}
			fieldAttrs={{ name: 'assetType' }}
			selectProps={{
				label: t('Asset Type'),
				required: true,
				error: !!errors.assetType,
				sx: { minWidth: '200px', maxWidth: '300px', width: '250px' },
			}}
			helperText={errors.assetType ? errors.assetType.toString() : ''}
		/>
	);
};

export const InspectionStandardField: React.FC = () => {
	const { t } = useGlobalization();
	const {
		values: { systemType, assetType },
		errors,
	} = useFormikContext<FailureDefinitionFormValues>();

	// Display read, so it uses the open-flag hooks. Pending and error leave the standards empty (field disabled).
	const { data: assetSchemaByType } = useAssetSchema(systemType, assetType);

	const inspectionStandards = useMemo(() => {
		const inspectionTypes = [];
		if (assetSchemaByType?.hasInspectionType('PACP')) {
			inspectionTypes.push({
				key: InspectionStandardType.PACP,
				value: InspectionStandardType.PACP,
				label: t('PACP'),
			});
		}
		if (assetSchemaByType?.hasInspectionType('WSA05')) {
			inspectionTypes.push({
				key: InspectionStandardType.WSA05,
				value: InspectionStandardType.WSA05,
				label: t('WSA05'),
			});
		}

		return inspectionTypes;
	}, [assetSchemaByType, t]);

	return (
		<InputSelect
			menuItems={inspectionStandards}
			fieldAttrs={{ name: 'inspectionStandard' }}
			selectProps={{
				label: t('Inspection Standard'),
				required: true,
				error: !!errors.inspectionStandard,
				disabled: inspectionStandards.length === 0,
				sx: { minWidth: '200px', maxWidth: '300px', width: '250px' },
			}}
			helperText={
				inspectionStandards.length === 0
					? 'Select an asset type first'
					: errors.inspectionStandard
					? errors.inspectionStandard.toString()
					: ''
			}
		/>
	);
};
